import { FC, useState, useEffect } from "react";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { IDocumentCreatorProps } from "../../../../@types/documents";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { ActRevaluationDataProvider } from "../../../../Services/DataProviders/ActRevaluationDataProvider";
import { IActRevaluationCreateDTO, IActRevaluationGetDTO, IActRevaluationUpdateDTO } from "../../../../libs/coreapi-dto/documents/ActRevaluation";
import { ActRevaluationForm } from "./ActRevaluationForm";
import { getActRevaluationItemUpdateDTO } from "../../../../libs/core-api-requests/customRequest/ActRevaluationItemUpdateRequest";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from 'uuid';
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { ProgressActionSpinner } from "../../../../components/progressActionSpinner/ProgressActionSpinner";
import { IActRevaluationItemEditDTO } from "../../../../libs/coreapi-dto/documents/ActRevaluationItem";
import { DocumentOperationSpinner } from "../../../../components/DocumentOperationSpinner/DocumentOperationSpinner";
import { re, reshape, resolve } from "mathjs";
import { reject } from "lodash";

interface IEditDocument {
    actRevaluation: IActRevaluationGetDTO
    documentUpdate: IActRevaluationUpdateDTO
    redirect: (id: string, view: GridStateType) => void
    refresh: (resolve?: (value: unknown) => void) => void
}

const ActRevaluationCreatorView: FC<IDocumentCreatorProps<IActRevaluationCreateDTO>> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext()
    const tabsContext = useTabsContext();
    const actRevaluationDP = new ActRevaluationDataProvider(appContext.coreApiService);
    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [id, setId] = useState<string | undefined>(props.idGlobal)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);

    const redirect = (id: string, view: GridStateType) => {
        setId(id);
        setViewState(view);
    }

    const refresh = (resolve?: (value: unknown) => void) => {
        setIsLoading(true)
        actRevaluationDP.getById(id as string, (actRevaluation) => {
            
            getActRevaluationItemUpdateDTO(actRevaluation.idGlobal as string, (revaluationEditItems: IActRevaluationItemEditDTO[]) => {
                const documentUpdate: IActRevaluationUpdateDTO = {
                    idGlobal: actRevaluation.idGlobal as string,
                    documentDate: actRevaluation.documentDate.toF3DateTimeServerSide(userContext),
                    idStoreGlobal: actRevaluation.store.idGlobal,
                    idPricingModelGlobal: actRevaluation.pricingModel.idGlobal,
                    mnemocode: actRevaluation.mnemocode,
                    comment: actRevaluation.comment,
                    newBarcode: actRevaluation.newBarcode,
                    idPriceBaseDocGlobal: actRevaluation?.priceBaseDoc?.idGlobal ?? undefined,
                    idActRevaluationReasonGlobal: actRevaluation?.revaluationReason?.idGlobal ?? undefined,
                    items: revaluationEditItems
                }
                setEditDocument(
                    {
                        actRevaluation: actRevaluation,
                        documentUpdate: documentUpdate,
                        redirect,
                        refresh: () => refresh(resolve)
                    })
                resolve?.(null)
                setIsLoading(false)
            });
        });
    }

    useEffect(() => {
        const initializeComponent = async () => {
            setIsLoading(true)

            if (viewState === 'edit') {
                try {
                    await new Promise((resolve, reject) => {
                        refresh(resolve);
                    });
                } catch (error) {
                    console.error('Error loading document:', error);
                } finally {
                    setIsLoading(false);
                }
            } else if (viewState === 'copy') {
                try {
                    await new Promise((resolve, reject) => {
                        actRevaluationDP.getById(id as string, (actRevaluation) => {
                            let idGlobal = uuidv4()
                            const documentUpdate: IActRevaluationUpdateDTO = {
                                items: [],
                                documentDate: DateTime.now().toF3DateTimeServerSide(userContext),
                                mnemocode: "",
                                comment: "",
                                newBarcode: false,
                                idPricingModelGlobal: actRevaluation.pricingModel.idGlobal,
                                idPriceBaseDocGlobal: "",
                                idActRevaluationReasonGlobal: "",
                                idGlobal: idGlobal
                            }
                            setEditDocument(
                                { 
                                    actRevaluation: {  idGlobal: idGlobal ,items: [] as any[] } as IActRevaluationGetDTO, 
                                    documentUpdate: documentUpdate,
                                    redirect,
                                    refresh: () => refresh(resolve)
                                }
                            )
                            resolve(null)
                        })
                    })
                } catch (error) {
                    console.error('Error loading document:', error);

                } finally {
                    setIsLoading(false)
                }
            }
            else {
                setIsLoading(false)
            }           
        }
        initializeComponent()
    }, [viewState])

    if (isLoading) {
        return (
            <div >
                <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />
            </div>
        );
    }

    return (
        <>
            {
                viewState === "create" ?
                    <ActRevaluationForm
                        isSubmitting={isSubmitting}
                        document={null}
                        documentItems={null}
                        documentUpdate={null}
                        idGlobal={props.idGlobal}
                        variant="create"
                        save={(RevaluationAct) => {
                            setIsSubmitting(true)
                            actRevaluationDP.create(RevaluationAct, (id) => {
                                setId(id)
                                setViewState('edit');
                                props.createdCallback?.()
                                setIsSubmitting(false)
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            })
                        }}
                        ok={(RevaluationAct) => {
                            setIsSubmitting(true)
                            actRevaluationDP.create(RevaluationAct, (id) => {
                                setId(id)
                                setViewState("create")
                                props.createdCallback?.()
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)
                                setIsSubmitting(false)
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            })
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                        redirect={(id, target) => redirect(id, target as GridStateType)}
                        refresh={refresh}
                        permission={props.permission}
                    />
                    :
                    viewState === "edit" ?
                        editDocument && <ActRevaluationForm
                            isSubmitting={isSubmitting}
                            variant='edit'
                            save={(ActRevaluation) => {
                                setIsSubmitting(true)
                                actRevaluationDP.update(id as string, ActRevaluation, (e) => {
                                    if (e.respType === 'isFailed') return setIsSubmitting(false);

                                    props.createdCallback?.()
                                    setIsSubmitting(false)
                                })
                            }}
                            ok={(ActRevaluation) => {
                                setIsSubmitting(true)
                                actRevaluationDP.update(id as string, ActRevaluation, (e) => {
                                    if (e.respType === 'isFailed') return setIsSubmitting(false);

                                    props.createdCallback?.()
                                    tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)
                                    setIsSubmitting(false)
                                })
                            }}
                            cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                            document={editDocument.actRevaluation}
                            documentItems={editDocument.actRevaluation.items}
                            documentUpdate={editDocument.documentUpdate}
                            redirect={(id, target) => redirect(id, target as GridStateType)}
                            refresh={refresh}
                            permission={props.permission}
                        />
                        :
                        viewState === "copy" ?
                            editDocument && <ActRevaluationForm
                                isSubmitting={isSubmitting}
                                variant='copy'
                                save={(ActRevaluation) => {
                                    setIsSubmitting(true)
                                    actRevaluationDP.create(ActRevaluation, (id) => {
                                        setId(id)
                                        setViewState('edit')
                                        props.createdCallback?.()
                                        setIsSubmitting(false)
                                    }, ()=>{}, (e)=>{
                                        setIsSubmitting(false);
                                    })
                                }}
                                ok={(ActRevaluation) => {
                                    setIsSubmitting(true)
                                    actRevaluationDP.create(ActRevaluation, (id) => {
                                        setId(id)
                                        setViewState("create")
                                        props.createdCallback?.()
                                        tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)
                                        setIsSubmitting(false)
                                    }, ()=>{}, (e)=>{
                                        setIsSubmitting(false);
                                    })
                                }}
                                cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                                document={editDocument.actRevaluation}
                                documentItems={[]}
                                documentUpdate={editDocument.documentUpdate}
                                redirect={(id, target) => redirect(id, target as GridStateType)}
                                refresh={refresh}
                                permission={props.permission}
                            />
                            :
                            <></>
            }
        </>
    )
}

export default ActRevaluationCreatorView